import { toUnityFixed } from '../../../../helpers/unityHelper';
import { UnityEvent, waitForEventOnce } from '../../../../helpers/unityPubSub';
import { selectSelectedHeight } from '../../../../redux/slicers/productBuilder/productBuilder.slicer';
import { getAppState } from '../../../../redux/store';
import { disableGameObject, enableGameObject, getLocationSide } from '../ProductBuilderComponents/commonHelper';

export class UndoRedoButton {
  constructor({ undoRedoInstance }) {
    this.undoRedo = undoRedoInstance;
    this.pending = new Map();
    this.createAction = this.undoRedo.createAction;
    this.isProcessing = false;
    this.processingQueue = Promise.resolve();
    this.isUndoRedoInProgress = false;
    this._init();
  }

  _init() {
    this._pushButtonPropertyChange = this.createAction(
      ({ prevValue, nextValue, propertyName, componentType, buttonIdentifier, updateCallback }) => {
        // Normalize values for comparison
        const normalizedPrev = toUnityFixed(prevValue, 3);
        const normalizedNext = toUnityFixed(nextValue, 3);
        
        if (normalizedPrev === normalizedNext) return false;
        
        return {
          __ACTION__: 'BUTTON_PROPERTY_CHANGE',
          undo: async () => {
            // Queue this operation and wait for completion
            this.processingQueue = this.processingQueue.then(async () => {
              while (this.isProcessing) {
                await new Promise(resolve => setTimeout(resolve, 50));
              }
              this.isProcessing = true;
              this.isUndoRedoInProgress = true;
              
              // Blur any focused inputs before undo
              if (document.activeElement && document.activeElement.blur) {
                document.activeElement.blur();
              }
              
              try {
                await updateCallback?.(buttonIdentifier, propertyName, normalizedPrev);
              } catch (error) {
                console.error('Error in undo:', error);
              } finally {
                this.isProcessing = false;
                // Keep flag true for a bit to prevent immediate new actions
                setTimeout(() => {
                  this.isUndoRedoInProgress = false;
                }, 150);
              }
            });
            await this.processingQueue;
          },
          redo: async () => {
            // Queue this operation and wait for completion
            this.processingQueue = this.processingQueue.then(async () => {
              while (this.isProcessing) {
                await new Promise(resolve => setTimeout(resolve, 50));
              }
              this.isProcessing = true;
              this.isUndoRedoInProgress = true;
              
              // Blur any focused inputs before redo
              if (document.activeElement && document.activeElement.blur) {
                document.activeElement.blur();
              }
              
              try {
                await updateCallback?.(buttonIdentifier, propertyName, normalizedNext);
              } catch (error) {
                console.error('Error in redo:', error);
              } finally {
                this.isProcessing = false;
                // Keep flag true for a bit to prevent immediate new actions
                setTimeout(() => {
                  this.isUndoRedoInProgress = false;
                }, 150);
              }
            });
            await this.processingQueue;
          },
        };
      }
    );

    this._pushButtonSideChange = this.createAction(
      ({ prevSideValue, nextSideValue, buttonIdentifier, componentType, updateCallback }) => {
        if (prevSideValue?.value === nextSideValue?.value) return false;
        
        return {
          __ACTION__: 'BUTTON_SIDE_CHANGE',
          undo: () => {
            // For undo, we're currently at nextSideValue location, need to find by it and change to prevSideValue
            updateCallback?.(nextSideValue, prevSideValue);
          },
          redo: () => {
            // For redo, we're currently at prevSideValue location, need to find by it and change to nextSideValue
            updateCallback?.(prevSideValue, nextSideValue);
          },
        };
      }
    );

    // Replace button variant
    this._pushButtonReplace = this.createAction(
      ({
        oldButtonId,
        newButtonId,
        oldConfig,
        oldFilterTuftedButtonComponent,
        newConfig,
        newFilterTuftedButtonComponent,
        buttonIdentifier,
        componentType,
        refreshCallback,
      }) => {
        if (!oldButtonId || !newButtonId) return false;
        return {
          __ACTION__: 'BUTTON_REPLACE',
          undo: async () => {
            const disablePayload = {
              productBuilderModel: {
                operationType: 'Modify',
                tuftedButtonConfiguration: { ...newConfig },
              },
            };
            // First: Disable the new tape
            disableGameObject(disablePayload);

            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
            // Small delay to ensure Unity processes
            // await new Promise((resolve) => setTimeout(resolve, 20));
            const enablePayload = {
              productBuilderModel: {
                operationType: 'Modify',
                tuftedButtonConfiguration: { ...oldConfig },
              },
              propInfoCommandDatas: oldFilterTuftedButtonComponent?.map(({ objectName }) => ({
                objectName: objectName,
              })),
            };

            // Second: Enable the old tape
            enableGameObject(enablePayload);
            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
                        

            // Small delay to ensure Unity processes
            // await new Promise((resolve) => setTimeout(resolve, 20));

            // Refresh after both operations
            setTimeout(() => refreshCallback?.(), 10);
          },
          redo: async () => {
            // const filteredComponent = await getFilteredComponent();
            const disablePayload = {
              productBuilderModel: {
                operationType: 'Modify',
                tuftedButtonConfiguration: { ...oldConfig },
              },
            };

            // First: Disable the new tape
            disableGameObject(disablePayload);
            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
                        

            // Small delay to ensure Unity processes
            // await new Promise((resolve) => setTimeout(resolve, 20));
            const enablePayload = {
              productBuilderModel: {
                operationType: 'Modify',
                tuftedButtonConfiguration: { ...newConfig },
              },
              propInfoCommandDatas: newFilterTuftedButtonComponent?.map(({ objectName }) => ({
                objectName: objectName,
              })),
            };

            // Second: Enable the old tape
            enableGameObject(enablePayload);

            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
                        

            // Small delay to ensure Unity processes
            // await new Promise((resolve) => setTimeout(resolve, 20));

            // Refresh after both operations
            setTimeout(() => refreshCallback?.(), 10);
          },
        };
      }
    );

    // Delete button
    this._pushButtonDelete = this.createAction(
      ({
        deletedConfig,
        deletedFilterTuftedButtonComponent,
        componentType,
        refreshCallback,
      }) => {
        if (!deletedConfig) return false;
        return {
          __ACTION__: 'BUTTON_DELETE',
          undo: async () => {
            const enablePayload = {
              productBuilderModel: {
                operationType: 'Delete',
                tuftedButtonConfiguration: { ...deletedConfig },
              },
              propInfoCommandDatas: deletedFilterTuftedButtonComponent?.map(({ objectName }) => ({
                objectName: objectName,
              })),
            };

            // Enable the deleted button
            enableGameObject(enablePayload);

            // Small delay to ensure Unity processes
            // await new Promise((resolve) => setTimeout(resolve, 40));
            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
                        

            // Refresh after operation
            setTimeout(() => refreshCallback?.(), 10);
          },
          redo: async () => {
            const disablePayload = {
              productBuilderModel: {
                operationType: 'Delete',
                tuftedButtonConfiguration: { ...deletedConfig },
              },
            };

            // Disable the button again
            disableGameObject(disablePayload);

            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
               

            // Small delay to ensure Unity processes
            // await new Promise((resolve) => setTimeout(resolve, 40));

            // Refresh after operation
            setTimeout(() => refreshCallback?.(), 10);
          },
        };
      }
    );

    // Mirror button (enable/disable mirrored components)
    this._pushButtonMirror = this.createAction(
      ({
        mirrorType,
        oldFilterTuftedButtonComponent,
        newFilterTuftedButtonComponent,
        mirrorConfig,
        componentType,
        refreshCallback,
      }) => {
        if (!mirrorConfig || !newFilterTuftedButtonComponent?.length) return false;
        const selectedHeight = selectSelectedHeight(getAppState());  
        let tuftedButtonConfigurations = []
        
        if(mirrorType === "AllSides"){
           tuftedButtonConfigurations.push({...mirrorConfig, sideLocation: 'RightCenter'});
           tuftedButtonConfigurations.push({...mirrorConfig, sideLocation: 'LeftCenter'});
           tuftedButtonConfigurations.push({...mirrorConfig, sideLocation: 'BackCenter'});
           tuftedButtonConfigurations.push({...mirrorConfig, sideLocation: 'ForwardCenter'})
           tuftedButtonConfigurations = tuftedButtonConfigurations.filter(config => config.sideLocation !== mirrorConfig.sideLocation);
        }

        const newComponents = newFilterTuftedButtonComponent.filter(
          (newComp) =>
            !oldFilterTuftedButtonComponent?.some(
              (oldComp) =>
                oldComp.objectName === newComp.objectName &&
                oldComp.tuftButtonSideLocation === newComp.tuftButtonSideLocation
            )
        );
        if (!newComponents.length) return false;
        
        return {
          __ACTION__: 'BUTTON_MIRROR',
          undo: async () => {
            // Disable the newly mirrored components
            const disablePayload = {
              ...(mirrorType === 'AllSides'
                ? {
                    tuftedButtonConfigurations: tuftedButtonConfigurations,
                    tuftedButtonConfiguration: null,
                  }
                : {}),

              ...(mirrorType === 'AllSides'
                ? {
                    propInfoCommandDatas: newFilterTuftedButtonComponent.map(
                      ({ objectName, tuftButtonParentObjectName, tuftButtonSideLocation }) => ({
                        objectName: objectName,
                        parentTuftObjectName: tuftButtonParentObjectName,
                        tuftButtonSideLocation: tuftButtonSideLocation,
                      })
                    ),
                  }
                : {}),
              productBuilderModel: {
                height: selectedHeight?.value || '12',
                operationType: 'Delete',
                tuftedButtonConfiguration: {
                ...mirrorConfig,
                sideLocation: getLocationSide(mirrorConfig?.sideLocation),
                },
                ...(mirrorType === 'AllSides'
                ? {
                    tuftedButtonConfiguration: null,
                  }
                : {}),
              },
            };
            disableGameObject(disablePayload);
            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);

            // Refresh after operation
            refreshCallback?.()
            await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);

          },
          redo: async () => {
            // Re-enable the mirrored components
            const enablePayload = {
              productBuilderModel: {
                height: selectedHeight?.value || '12',
                operationType: 'Delete',
                tuftedButtonConfiguration: {
                ...mirrorConfig,
                sideLocation: getLocationSide(mirrorConfig?.sideLocation),
                },
                ...(mirrorType === 'AllSides'
                ? {
                    tuftedButtonConfiguration: null,
                  }
                : {}),
              },
              propInfoCommandDatas: newComponents.map(({ objectName }) => ({
                objectName: objectName,
              })),
              ...(mirrorType === 'AllSides'
                ? {
                    propInfoCommandDatas: newComponents.map(
                      ({ objectName, tuftButtonParentObjectName, tuftButtonSideLocation }) => ({
                        objectName: objectName,
                        parentTuftObjectName: tuftButtonParentObjectName,
                        tuftButtonSideLocation: tuftButtonSideLocation,
                      })
                    ),
                  }
                : {}),
              // tuftedButtonConfiguration: {
              //   ...mirrorConfig,
              //   sideLocation: getLocationSide(mirrorConfig?.sideLocation),
              // },
              ...(mirrorType === 'AllSides'
                ? {
                    tuftedButtonConfigurations: tuftedButtonConfigurations,
                    tuftedButtonConfiguration: null,
                  }
                : {}),
            };
            enableGameObject(enablePayload);
            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);

            // Refresh after operation
            refreshCallback?.()
            await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
          },
        };
      }
    );
  }

  clearPendingProperty(componentType, buttonIdentifier, propertyName) {
    const key = `${componentType}__${buttonIdentifier}__${propertyName}`;
    this.pending.delete(key);
  }

  startPropertyChange({ buttonIdentifier, propertyName, initialValue, componentType }) {
    const key = `${componentType}__${buttonIdentifier}__${propertyName}`;
    
    // Clear any existing pending change for this property
    this.pending.delete(key);
    
    this.pending.set(key, {
      buttonIdentifier,
      propertyName,
      initialValue: toUnityFixed(initialValue, 3),
      componentType,
      timestamp: Date.now(),
    });
  }

  updatePendingChange({ buttonIdentifier, propertyName, currentValue, componentType }) {
    const key = `${componentType}__${buttonIdentifier}__${propertyName}`;
    const item = this.pending.get(key);
    if (!item) return;
    item.lastValue = toUnityFixed(currentValue, 3);
    item.lastUpdateTimestamp = Date.now();
  }

  endPropertyChange({ buttonIdentifier, propertyName, finalValue, componentType, updateCallback }) {
    const key = `${componentType}__${buttonIdentifier}__${propertyName}`;
    const item = this.pending.get(key);
    if (!item) return;
    
    const prevValue = item.initialValue;
    const nextValue = toUnityFixed(finalValue, 3);
    
    // Only push if values are different
    if (prevValue !== nextValue) {
      this._pushButtonPropertyChange({
        prevValue,
        nextValue,
        propertyName,
        componentType,
        buttonIdentifier,
        updateCallback,
      });
    }
    
    this.pending.delete(key);
  }

  addSideChangeAction({ buttonIdentifier, prevSideValue, nextSideValue, componentType, updateCallback }) {
    this._pushButtonSideChange({
      prevSideValue,
      nextSideValue,
      buttonIdentifier,
      componentType,
      updateCallback,
    });
  }

  addButtonReplaceAction({ oldButtonId, newButtonId, oldConfig,oldFilterTuftedButtonComponent, newConfig, newFilterTuftedButtonComponent, buttonIdentifier, componentType, refreshCallback }) {
    this._pushButtonReplace({
      oldButtonId,
      newButtonId,
      oldConfig,
      oldFilterTuftedButtonComponent,
      newConfig,
      newFilterTuftedButtonComponent,
      buttonIdentifier,
      componentType,
      refreshCallback,
    });
  }

  addButtonDeleteAction({ deletedConfig, deletedFilterTuftedButtonComponent, componentType, refreshCallback }) {
    this._pushButtonDelete({
      deletedConfig,
      deletedFilterTuftedButtonComponent,
      componentType,
      refreshCallback,
    });
  }

  addButtonMirrorAction({ mirrorType, oldFilterTuftedButtonComponent, newFilterTuftedButtonComponent, mirrorConfig, componentType, refreshCallback }) {
    this._pushButtonMirror({
      mirrorType,
      oldFilterTuftedButtonComponent,
      newFilterTuftedButtonComponent,
      mirrorConfig,
      componentType,
      refreshCallback,
    });
  }

  clearAll() {
    this.pending.clear();
    this.isProcessing = false;
    this.isUndoRedoInProgress = false;
    this.processingQueue = Promise.resolve();
  }
}
